import { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
	Card,
	CardContent,
	Button,
	Typography,
	TextField,
	CircularProgress,
	Box,
	Grid,
	Stack,
	Link,
	CardActionArea,
	useTheme,
	useMediaQuery,
	FormHelperText,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { useDispatch } from 'react-redux';
import { clearUser } from '../../services/features/authApi/authSlice';
import PasswordField from '../PasswordField';
import LoginPageLayout from '../LoginPageLayout';
import {
	useForgotPasswordMutation,
	useResetPasswordMutation,
} from '../../services/features/authApi/authApi';
import { GENERIC_ERROR } from '../../helper/constants';

/**
 * Yup validation schema for the reset password form.
 */
const passwordResetValidationSchema = Yup.object({
	password: Yup.string().min(4).max(25).required('Please enter your new password'),
	passwordRepeat: Yup.string()
		.oneOf([Yup.ref('password'), null], 'Password must match')
		.required('Please confirm your new password'),
});

const validationSchema = Yup.object({
	email: Yup.string().email('Email must be a valid email').required('Please enter your email'),
});

const ResetPasswordPage = () => {
	const theme = useTheme();

	const [searchParams] = useSearchParams();

	const token = searchParams.get('token');
	const email = searchParams.get('email');

	// useEffect(() => console.log(token, email), [token, email]);
	const isResetPassword = token != null && email != null;

	const [resetPassword] = useResetPasswordMutation();
	const [forgotPassword] = useForgotPasswordMutation();

	const initialValues = {
		password: '',
		passwordRepeat: '',
		email: '',
		successMessage: '',
	};

	const dispatch = useDispatch();

	const [loading, setLoading] = useState(false);
	const [genericError, setGenericError] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string[] | string>('');

	const navigate = useNavigate();

	// Formik form handling
	const formik = useFormik({
		initialValues,
		validationSchema: isResetPassword ? passwordResetValidationSchema : validationSchema,
		onSubmit: async (values, { setFieldValue }) => {
			setGenericError(false);
			setLoading(true);
			try {
				let res;
				if (isResetPassword) {
					res = await resetPassword({
						Email: email,
						Token: token,
						Password: values.password,
					}).unwrap();
					navigate('/');
				} else {
					res = await forgotPassword(values.email).unwrap();
				}
				// console.log('RES', res);
				setFieldValue(
					'successMessage',
					typeof res === 'string' ? res : 'Password reset successfully'
				);
			} catch (e: any) {
				console.log(
					`Error ${
						isResetPassword ? 'with password reset' : 'with forgot password request'
					}`,
					e
				);
				setFieldValue('successMessage', '');
				console.log('Error with reset password', e);

				// const error =
				// 	typeof e?.data == 'string'
				// 		? e?.data
				// 		: Array.isArray(e?.data)
				// 		? e?.data.join('\n')
				// 		: GENERIC_ERROR;
				setGenericError(true);
				setErrorMessage(e?.data ?? GENERIC_ERROR);
			} finally {
				setLoading(false);
			}
		},
	});

	return (
		<>
			<LoginPageLayout
				cardTitle='Reset Password'
				onSubmit={(e: any) => {
					e.preventDefault();
					formik.handleSubmit();
				}}
				HeaderComponent={
					<Link
						to={`/`}
						component={RouterLink}
						variant='subtitle1'
						sx={{
							textDecorationColor: '#474848',
							color: '#474848',
							textAlign: 'right',
						}}
					>
						Back to Login
					</Link>
				}
				SubtitleComponent={
					isResetPassword
						? 'Please enter your new password'
						: 'Enter your email to receive a link to reset your password'
				}
				FooterActions={
					<Button type='submit' variant='contained'>
						{loading ? (
							<CircularProgress size={'2em'} color='inherit' />
						) : isResetPassword ? (
							'Reset Password'
						) : (
							'Get Password Reset Link'
						)}
					</Button>
				}
			>
				<>
					{isResetPassword ? (
						<>
							<PasswordField
								label='Password'
								{...formik.getFieldProps('password')}
								fullWidth
								error={
									(formik.touched.password && Boolean(formik.errors.password)) ||
									genericError
								}
								helperText={formik.touched.password && formik.errors.password}
								disabled={loading}
							/>
							<PasswordField
								label='Re-enter password'
								{...formik.getFieldProps('passwordRepeat')}
								fullWidth
								error={
									(formik.touched.passwordRepeat &&
										Boolean(formik.errors.passwordRepeat)) ||
									genericError
								}
								helperText={
									formik.touched.passwordRepeat && formik.errors.passwordRepeat
								}
								disabled={loading}
							/>
							{genericError && (
								<Stack>
									{typeof errorMessage == 'string' ? (
										<FormHelperText error>{errorMessage}</FormHelperText>
									) : (
										errorMessage.map((e, i) => (
											<FormHelperText key={`error-message-${i}`} error>
												{e}
											</FormHelperText>
										))
									)}
								</Stack>
							)}
						</>
					) : (
						<TextField
							label='Email address'
							{...formik.getFieldProps('email')}
							fullWidth
							error={
								(formik.touched.email && Boolean(formik.errors.email)) ||
								genericError
							}
							helperText={
								genericError
									? errorMessage
									: formik.touched.email && formik.errors.email
							}
							disabled={loading}
						/>
					)}
					{(formik.values.successMessage ?? '').length > 0 && (
						<FormHelperText sx={{ color: 'success' }}>
							{formik.values.successMessage}
						</FormHelperText>
					)}
				</>
			</LoginPageLayout>
		</>
	);
};

export default ResetPasswordPage;
